import React, { useMemo, useState } from 'react'
import { Button } from '@resiliatech/birdsong'
import { useQuery } from '@apollo/client'
import { Box } from '@mui/material'
import GET_ACADEMY_RESOURCES from 'ApolloAcademy/Queries/GetAcademyResources'
import { TasksByGroupTabs } from 'Components/Pages/Impact/ImpactReportDashboard/TaskDigest/TasksByGroupTabs'
import {
  Tab,
  TabFilterKeyEnum,
} from 'Components/Pages/Impact/ImpactReportDashboard/types'
import { ACADEMY_CONTEXT } from 'Components/Pages/Academy/_common/utils'
import { useAppState } from 'Context/AppContext/AppContext'
import { useSnackbar } from 'Context/SnackbarContext/SnackbarContext'
import { useShowUpgradeModalDialog } from 'Hooks/useGlobalDialogDispatchers'
import {
  ChecklistItem,
  Goal,
  RecurringChecklistItem,
} from 'Generated/graphql-types'
import {
  ResourceSortOptions,
  ResourceType,
} from 'Generated/academy-graphql-types'
import { ChecklistItemWithResourceInfo, User } from 'TypeDefs/Goal'
import useNonprofitOrgTeam from 'Hooks/useNonprofitOrgTeam/useNonprofitOrgTeam'
import { TaskListV2 } from './TaskListV2'
import {
  ButtonContainer,
  ContentWrapper,
  Header,
  HeaderAndButtonContainer,
  HeaderContainer,
  Subheader,
  TaskListContainer,
} from '../RecurringTasks.styled'
import { TaskModalV2 } from '../TaskModalV2/TaskModalV2'
import {
  countVisibleTasks,
  getTabName,
  getTasksWithResources,
  getFilteredTasksByTab,
  useTaskUpdate,
  useUpsertRecurringTask,
  getTasksForTabbedView,
  buildTaskToastMessage,
} from '../utils'
import { TaskListEmptyState } from './TaskListEmptyState'
import { taskAnalytics } from '../analytics.utils'
import { UpsertTaskHandler } from '../types'
import { ProgressUpdates } from '../../ProgressUpdate/ProgressUpdates/ProgressUpdates'

type TaskListWithTabsProps = {
  goal: Goal
  simple?: boolean
}

export const TaskListWithTabs = ({ goal, simple }: TaskListWithTabsProps) => {
  const {
    nonprofitOrgConfig: {
      orgId: nonprofitOrgId,
      platformPlan,
      funderOrgId,
      role: userRole,
    },
    userConfig: { id: userId, email: userEmail },
  } = useAppState()
  const { popToast } = useSnackbar()
  const { teamMap = {} } = useNonprofitOrgTeam()
  const dispatchShowUpgradeModal = useShowUpgradeModalDialog()
  const { upsertRecurringTask, loading: upsertRecurringLoading } =
    useUpsertRecurringTask(goal.id, nonprofitOrgId)
  const { upsertTask, loading: upsertLoading } = useTaskUpdate(
    goal.id,
    nonprofitOrgId
  )

  const getAssigneeEmail = (user: User | null | undefined) =>
    user?.id ? teamMap[user.id]?.email : null

  const baseEventProps = {
    assignerEmail: userEmail,
    assignerId: userId,
    funderOrgId,
    goalId: goal.id,
    goalType: goal.type,
    isGoalSharedWithFunder: !!goal.FunderOrgToSharedGoal,
    nonprofitOrgId,
    platformPlan,
    templateLibraryId: goal.templateLibraryId || null,
    userId,
    userRole,
  }

  const [selectedTask, setSelectedTask] =
    useState<ChecklistItemWithResourceInfo>()
  const [isDuplicating, setIsDuplicating] = useState(false)
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(TabFilterKeyEnum.all)
  const [showProgressUpdates, setShowProgressUpdates] = useState(false)
  const [activeTask, setActiveTask] =
    useState<Pick<ChecklistItemWithResourceInfo, 'id'>>()

  const handleResetTaskState = () => {
    setIsTaskModalOpen(false)
    setIsDuplicating(false)
    setSelectedTask(undefined)
  }

  const handleSelectTask = (task: ChecklistItemWithResourceInfo) => {
    const isRecurring = !!task.checklistItemGroupId
    if (isRecurring) {
      taskAnalytics.dispatchEditRecurringTaskClicked({
        ...baseEventProps,
        assigneeUserEmail: getAssigneeEmail(task.owner) || '',
        dueDate: task.dueDate ?? null,
        ownerId: task.owner?.id ?? null,
        taskId: task.id,
        taskUpdatedDate: task.updatedAt,
      })
    } else {
      taskAnalytics.dispatchGoalTaskEditTaskClicked({
        ...baseEventProps,
        assigneeUserEmail: getAssigneeEmail(task.owner) || '',
        dueDate: task.dueDate ?? null,
        ownerId: task.owner?.id ?? null,
        taskId: task.id,
        taskUpdatedDate: task.updatedAt,
      })
    }
    setSelectedTask(task)
    setIsTaskModalOpen(true)
  }

  const handleDuplicateTask = (task: ChecklistItemWithResourceInfo) => {
    const isRecurring = !!task.checklistItemGroupId
    if (isRecurring) {
      taskAnalytics.dispatchDuplicateRecurringTaskClicked({
        ...baseEventProps,
        assigneeUserEmail: getAssigneeEmail(task.owner) || '',
        dueDate: task.dueDate ?? null,
        ownerId: task.owner?.id ?? null,
        taskId: task.id,
        taskUpdatedDate: task.updatedAt,
      })
    } else {
      taskAnalytics.dispatchGoalTaskDuplicateTaskClicked({
        ...baseEventProps,
        assigneeUserEmail: getAssigneeEmail(task.owner) || '',
        dueDate: task.dueDate ?? null,
        ownerId: task.owner?.id ?? null,
        taskId: task.id,
        taskUpdatedDate: task.updatedAt,
      })
    }
    setSelectedTask(task)
    setIsDuplicating(true)
    setIsTaskModalOpen(true)
  }

  const handleUpsertTask: UpsertTaskHandler = async (
    taskInfo: Partial<ChecklistItemWithResourceInfo>,
    recurrenceOptions?: RecurringChecklistItem,
    shouldFireCreateEvent = false,
    shouldPopToast = false,
    isRecurring = false
  ) => {
    if (!taskInfo.title) return
    if (taskInfo.isCompleted) {
      setActiveTask(taskInfo)
      setShowProgressUpdates(true)
    }

    const updates = taskInfo

    // Ensure resource is not gated by plan
    if ('resource' in taskInfo && taskInfo.resource?.gatedByPlan) {
      dispatchShowUpgradeModal(true)
      return
    }

    // Ensure URL is fully qualified
    if ('linkUrl' in taskInfo) {
      const { linkUrl } = taskInfo
      if (linkUrl && !linkUrl.includes('http'))
        updates.linkUrl = `https://${linkUrl}`
    }

    // Clear the ID, group ID, and completion values when duplicating tasks
    const duplicatingModifiers = isDuplicating
      ? {
          id: undefined,
          checklistItemGroupId: null,
          isCompleted: false,
          completedAt: null,
        }
      : {}

    const taskInfoToSave = {
      ...selectedTask,
      ...updates,
      ...duplicatingModifiers,
    }

    const toastMessage = buildTaskToastMessage({
      isRecurring,
      isDuplicating,
      selectedTask,
    })

    if (isRecurring && recurrenceOptions) {
      const recurringTasks = await upsertRecurringTask(
        taskInfoToSave,
        recurrenceOptions
      )
      recurringTasks.forEach((task: Partial<ChecklistItem>) => {
        if (shouldFireCreateEvent) {
          taskAnalytics.dispatchGoalTaskCreated({
            ...baseEventProps,
            assigneeUserEmail: getAssigneeEmail(task.owner),
            dueDate: task.dueDate,
            ownerId: task.owner?.id,
            taskDescription: task.title ?? '',
            taskId: task.id,
            taskUpdatedDate: task.updatedAt,
            dayOfWeek: recurrenceOptions.dayOfWeek ?? undefined,
            dayOfMonth: recurrenceOptions.dayOfMonth ?? undefined,
            endDate: recurrenceOptions.endDate ?? undefined,
            frequency: recurrenceOptions.frequency,
            recurrenceType: recurrenceOptions.recurrenceType,
            startDate: recurrenceOptions.startDate,
            weekOfMonth: recurrenceOptions.weekOfMonth ?? undefined,
          })
        }
      })
    } else {
      const upsertedTask = await upsertTask(taskInfoToSave)
      if (selectedTask) {
        taskAnalytics.dispatchGoalTaskEdited({
          ...baseEventProps,
          assigneeUserEmail: getAssigneeEmail(selectedTask.owner),
          dueDate: upsertedTask.dueDate,
          ownerId: upsertedTask.owner?.id,
          taskDescription: upsertedTask.title,
          taskId: selectedTask.id,
          taskUpdatedDate: selectedTask.updatedAt,
        })
      } else if (shouldFireCreateEvent) {
        taskAnalytics.dispatchGoalTaskCreated({
          ...baseEventProps,
          assigneeUserEmail: getAssigneeEmail(upsertedTask.owner),
          dueDate: upsertedTask.dueDate,
          ownerId: upsertedTask.owner?.id,
          taskDescription: upsertedTask.title,
          taskId: upsertedTask.id,
          taskUpdatedDate: upsertedTask.updatedAt,
        })
      }
    }

    taskAnalytics.dispatchGoalTaskSaveClicked(
      taskInfoToSave as ChecklistItemWithResourceInfo,
      baseEventProps
    )

    if (shouldPopToast) popToast({ content: toastMessage })
    handleResetTaskState()
  }

  const handleAddTask = () => {
    taskAnalytics.dispatchGoalAddTaskClicked(baseEventProps)
    setSelectedTask(undefined)
    setIsTaskModalOpen(true)
  }

  const handleChangeTab = (tab: Tab) => {
    const tabFilterKey = tab.filterKey
    taskAnalytics.dispatchTabClicked(baseEventProps, tabFilterKey)
    setActiveTab(tab.filterKey)
  }

  const tasks = useMemo(() => goal.ChecklistItem || [], [goal.ChecklistItem])

  const getResourceIdsForItems = useMemo(
    () =>
      tasks
        .map((checklistItem: ChecklistItem) => checklistItem.resourceId)
        .filter((resourceId): resourceId is string => resourceId !== null),
    [tasks]
  )

  const { data: resourceData, loading: resourceLoading } = useQuery(
    GET_ACADEMY_RESOURCES,
    {
      ...ACADEMY_CONTEXT,
      variables: {
        ids: getResourceIdsForItems,
        resourceType: [
          ResourceType.Article,
          ResourceType.Course,
          ResourceType.Template,
          ResourceType.LearningTrack,
        ],
        query: '',
        sort: ResourceSortOptions.Random,
        anonymousId: global.analytics?.user?.()?.anonymousId?.(),
      },
      skip: getResourceIdsForItems.length === 0,
    }
  )

  const tasksWithResources = useMemo(
    () =>
      getTasksWithResources({
        tasks,
        resources: resourceData?.resources?.nodes || [],
      }),
    [tasks, resourceData]
  )

  const {
    assignedToMeTasks,
    completedTasks,
    pastDueTasks,
    todo,
    unassignedTasks,
  } = getFilteredTasksByTab(tasksWithResources, userId)

  const visibleTasksCount = useMemo(
    () => countVisibleTasks(tasksWithResources),
    [tasksWithResources]
  )

  const tabs: Tab[] = [
    {
      count: unassignedTasks.length,
      filterKey: TabFilterKeyEnum.unassigned,
      label: 'Unassigned',
    },
    {
      count: pastDueTasks.length,
      filterKey: TabFilterKeyEnum.pastDue,
      label: 'Past Due',
    },
    {
      count: todo.length,
      filterKey: TabFilterKeyEnum.todo,
      label: 'To Do',
    },
    {
      count: completedTasks.length,
      filterKey: TabFilterKeyEnum.completed,
      label: 'Completed',
    },
    {
      count: assignedToMeTasks.length,
      filterKey: TabFilterKeyEnum.assignedToMe,
      label: 'Assigned to Me',
    },
    { count: visibleTasksCount, filterKey: TabFilterKeyEnum.all, label: 'All' },
  ]

  const tabToTasksMap: Record<
    TabFilterKeyEnum,
    ChecklistItemWithResourceInfo[]
  > = {
    [TabFilterKeyEnum.all]: tasksWithResources,
    [TabFilterKeyEnum.assignedToMe]: assignedToMeTasks,
    [TabFilterKeyEnum.completed]: completedTasks,
    [TabFilterKeyEnum.pastDue]: pastDueTasks,
    [TabFilterKeyEnum.todo]: todo,
    [TabFilterKeyEnum.unassigned]: unassignedTasks,
  }

  const activeTasks = tabToTasksMap[activeTab]
  const viewableTasks = getTasksForTabbedView(activeTasks, activeTab)
  const currentTab = getTabName(activeTab)
  const isEmptyState = !viewableTasks || viewableTasks.length === 0

  return (
    <>
      {isTaskModalOpen && (
        <TaskModalV2
          simple={simple}
          goal={goal}
          isDuplicating={isDuplicating}
          isLoading={upsertLoading || upsertRecurringLoading}
          isTaskModalOpen={isTaskModalOpen}
          onCloseModal={handleResetTaskState}
          onUpsertTask={handleUpsertTask}
          task={selectedTask}
        />
      )}
      <ContentWrapper>
        <HeaderAndButtonContainer>
          {!simple && (
            <HeaderContainer>
              <Header>Tasks</Header>
              <Subheader>
                A task can be any steps you and your organization are taking, or
                things you&apos;re learning that are making an impact towards
                achieving this goal. Achieve success faster by assigning tasks
                across your organization.
              </Subheader>
            </HeaderContainer>
          )}
          <ButtonContainer>
            <Button
              variant='primary'
              label='Add Task'
              onClick={handleAddTask}
            />
          </ButtonContainer>
        </HeaderAndButtonContainer>
        <TaskListContainer className='scroll'>
          <TasksByGroupTabs
            activeTab={activeTab}
            onChangeTab={handleChangeTab}
            tabs={tabs}
          />
          {isEmptyState ? (
            <TaskListEmptyState tabName={currentTab} />
          ) : (
            <TaskListV2
              goal={goal}
              isResourceLoading={resourceLoading}
              onDuplicateTask={handleDuplicateTask}
              onEditTask={handleSelectTask}
              onUpsertTask={handleUpsertTask}
              tasks={viewableTasks}
            />
          )}
        </TaskListContainer>
      </ContentWrapper>
      <Box position='relative'>
        {activeTask && (
          <ProgressUpdates
            open={showProgressUpdates}
            onClose={() => setShowProgressUpdates(false)}
            goalId={goal.id}
            goal={goal}
            taskId={activeTask.id}
          />
        )}
      </Box>
    </>
  )
}
